import { useEffect, type ReactNode } from "react";

const EFFECTIVE_DATE = "August 26, 2026";
const CONTACT_URL = "https://www.gordonpeterson.com/contact";

type LegalPageProps = {
  title: string;
  children: ReactNode;
};

function LegalPage({ title, children }: LegalPageProps) {
  useEffect(() => {
    document.title = `${title} | Daily Groove`;
  }, [title]);

  return (
    <main className="min-h-screen bg-[#faf9f7] px-4 py-8 text-slate-800 sm:px-6 sm:py-12 lg:px-8">
      <article className="mx-auto max-w-3xl rounded-2xl border border-slate-200 bg-white px-6 py-8 shadow-sm sm:px-10 sm:py-12">
        <header className="border-b border-slate-200 pb-7">
          <a
            href="/"
            className="inline-flex items-center text-sm font-semibold text-slate-600 transition-colors hover:text-slate-950"
          >
            ← Daily Groove
          </a>
          <p className="mt-8 text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">
            Daily Groove
          </p>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
            {title}
          </h1>
          <p className="mt-4 text-sm text-slate-500">Effective date: {EFFECTIVE_DATE}</p>
        </header>
        <div className="legal-copy pt-8 text-[15px] leading-7 text-slate-700">{children}</div>
      </article>
    </main>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-9">
      <h2 className="text-xl font-semibold tracking-tight text-slate-950">{title}</h2>
      <div className="mt-3 space-y-4">{children}</div>
    </section>
  );
}

function ContactLink() {
  return (
    <a className="font-medium text-teal-700 underline underline-offset-2 hover:text-teal-900" href={CONTACT_URL}>
      contact form
    </a>
  );
}

export function PrivacyPolicy() {
  return (
    <LegalPage title="Privacy Policy">
      <p>
        Daily Groove is a personal productivity service operated by Gordon Peterson ("Daily Groove," "we," "us," and
        "our"). This Privacy Policy explains what information we collect, how we use it, and the choices available to
        you when you use <a className="font-medium text-teal-700 underline underline-offset-2 hover:text-teal-900" href="https://groove.gordonpeterson.com">groove.gordonpeterson.com</a> and
        related Daily Groove services (collectively, the "Service").
      </p>

      <Section title="1. Information we collect">
        <p>
          We collect information you provide to create and use an account. This includes your name, email address, and
          a securely derived password hash when you choose email-and-password registration. We do not store the
          readable version of your password.
        </p>
        <p>
          When you choose Google sign-in, we receive the identity information supplied through the authentication
          flow: a provider account identifier, your verified email address when available, your name when available,
          and the identity provider used. Daily Groove currently uses Google sign-in only to authenticate you. It does
          not request access to your Gmail, Google Calendar, Google Drive, contacts, or other Google service content
          through that sign-in flow.
        </p>
        <p>
          We also collect the content you create in the Service, including lists, task templates, task instances,
          dates and schedules, task details, notes, tags, completion and status information, and display preferences.
          If you enable calendar export, we create and store calendar-feed data generated from that content and an
          account-specific feed token used to retrieve it.
        </p>
        <p>
          Like most online services, our web infrastructure may receive basic technical information when you connect,
          such as IP address, browser and device information, request time, requested URL, and error or security-log
          information. We use this information for security, reliability, troubleshooting, and service operation.
        </p>
      </Section>

      <Section title="2. How we use information">
        <p>
          We use your information to create and secure your account; provide, maintain, and improve the Service;
          display and organize your task data; generate calendar exports you request; respond to you; detect,
          investigate, and prevent security or misuse issues; and comply with applicable legal obligations.
        </p>
        <p>
          We do not sell your personal information. We do not use your Daily Groove task content for targeted
          advertising, and we do not use Google identity data for advertising or any purpose unrelated to
          authentication and operation of the Service.
        </p>
      </Section>

      <Section title="3. How information is shared">
        <p>
          We share information only as needed to operate the Service, comply with law, protect rights and security,
          or carry out a business transfer. Service providers may process information on our behalf under their own
          applicable terms and privacy practices. Current providers include infrastructure and database providers
          that host the Service and its data, Supabase for social-authentication support, and Google when you choose
          Google sign-in.
        </p>
        <p>
          Calendar feeds deserve special care. A feed is accessed with a long, account-specific URL rather than an
          interactive Daily Groove sign-in. Anyone who obtains that URL may be able to access the calendar data it
          returns, so please treat a calendar-feed URL as sensitive and do not share it publicly.
        </p>
      </Section>

      <Section title="4. Cookies and similar technology">
        <p>
          Daily Groove uses an essential, HTTP-only session cookie to keep you signed in. This cookie is necessary for
          account functionality and is not used for cross-site advertising. You can configure your browser to remove
          or block cookies, but doing so may prevent the Service from keeping you signed in or working correctly.
        </p>
      </Section>

      <Section title="5. Data retention, access, and deletion">
        <p>
          We retain account and Service data while your account remains active and afterward only for as long as
          reasonably necessary for security, dispute resolution, legal compliance, and legitimate operational
          purposes. You may request access to, correction of, export of, or deletion of personal information
          associated with your account through our <ContactLink />. We may need to verify your identity before acting
          on a request, and certain records may be retained where law or legitimate security needs require it.
        </p>
      </Section>

      <Section title="6. Security">
        <p>
          We use reasonable administrative, technical, and organizational measures intended to protect information.
          These include HTTPS for the public Service, HTTP-only secure session handling, password hashing for local
          accounts, and access controls for Service data. No online service or transmission is completely secure, so
          we cannot guarantee absolute security.
        </p>
      </Section>

      <Section title="7. International use">
        <p>
          Daily Groove is operated from the United States. If you access the Service from another country, your
          information may be processed in the United States or other locations where our service providers operate.
        </p>
      </Section>

      <Section title="8. Children">
        <p>
          Daily Groove is not directed to children under 13, and we do not knowingly collect personal information
          from children under 13. If you believe a child has provided personal information to us, please contact us so
          we can take appropriate action.
        </p>
      </Section>

      <Section title="9. Changes to this Privacy Policy">
        <p>
          We may update this Privacy Policy as the Service or applicable requirements change. We will post the
          updated version here and revise the effective date. Where required, we will provide additional notice or
          obtain consent before applying a material change.
        </p>
      </Section>

      <Section title="10. Contact">
        <p>
          For privacy questions or requests, please use the operator's <ContactLink /> and identify your request as
          relating to Daily Groove Privacy.
        </p>
      </Section>
    </LegalPage>
  );
}

export function TermsOfService() {
  return (
    <LegalPage title="Terms of Service">
      <p>
        These Terms of Service ("Terms") govern your access to and use of Daily Groove, including
        <a className="font-medium text-teal-700 underline underline-offset-2 hover:text-teal-900" href="https://groove.gordonpeterson.com"> groove.gordonpeterson.com</a> and related features (the "Service").
        By creating an account or using the Service, you agree to these Terms. If you do not agree, do not use the
        Service.
      </p>

      <Section title="1. The Service">
        <p>
          Daily Groove is a productivity tool for creating and organizing personal lists, task templates, scheduled
          tasks, notes, tags, and related calendar exports. The Service is currently provided without a paid
          subscription or payment-processing feature. Any future paid feature will be presented with its applicable
          pricing and terms before payment is required.
        </p>
      </Section>

      <Section title="2. Eligibility and accounts">
        <p>
          You must be legally able to enter into these Terms and must provide accurate account information. You are
          responsible for protecting your account credentials and for activity that occurs through your account. Tell
          us promptly through our <ContactLink /> if you believe your account has been accessed without permission.
        </p>
        <p>
          You may sign in with a local email-and-password account or, where available, an approved social identity
          provider. Your use of a third-party identity provider is also subject to that provider's terms and privacy
          practices.
        </p>
      </Section>

      <Section title="3. Your content and responsibility">
        <p>
          You retain ownership of the task information, notes, and other content you submit to Daily Groove ("User
          Content"). You grant us the limited, non-exclusive right to host, process, reproduce, and display User
          Content only as necessary to operate, secure, and improve the Service for you.
        </p>
        <p>
          You are responsible for User Content and for ensuring that you have the rights needed to submit it. Do not
          use the Service to upload or distribute unlawful, infringing, harmful, deceptive, abusive, or privacy-
          violating content, or content that compromises another person's security or rights.
        </p>
      </Section>

      <Section title="4. Acceptable use">
        <p>
          You may use the Service only in accordance with these Terms and applicable law. You may not interfere with
          or disrupt the Service; bypass access controls; attempt unauthorized access; probe or scan for
          vulnerabilities without authorization; use automated means that impose an unreasonable load; introduce
          malware; impersonate another person; or use the Service to violate others' rights.
        </p>
      </Section>

      <Section title="5. Calendar exports">
        <p>
          Calendar exports are provided for your convenience. You are responsible for safeguarding any feed URL or
          token associated with your account. You understand that a person who obtains a calendar-feed URL may be
          able to view information included in that feed. Do not share a feed URL publicly or with people you do not
          authorize to view the corresponding information.
        </p>
      </Section>

      <Section title="6. Availability and changes">
        <p>
          We may update, modify, suspend, or discontinue all or part of the Service to maintain it, address security
          or legal needs, or improve its functionality. We will use reasonable efforts to provide notice of material
          changes when practical, but we do not guarantee that the Service will always be available, uninterrupted,
          error-free, or compatible with every device or service.
        </p>
      </Section>

      <Section title="7. Suspension and termination">
        <p>
          You may stop using the Service at any time. We may suspend or terminate access if we reasonably believe you
          have violated these Terms, created risk for the Service or others, or if we must do so to comply with law.
          We may also discontinue the Service, subject to applicable law. To request account deletion, use our
          <ContactLink />.
        </p>
      </Section>

      <Section title="8. Intellectual property">
        <p>
          Except for User Content, the Service and its software, design, branding, and other materials are owned by
          Daily Groove or its licensors and are protected by applicable intellectual-property laws. Subject to these
          Terms, we grant you a limited, revocable, non-transferable license to use the Service for its intended
          purpose. These Terms do not grant you rights to copy, modify, reverse engineer, distribute, or commercially
          exploit the Service except where applicable law expressly permits it.
        </p>
      </Section>

      <Section title="9. Disclaimers">
        <p>
          The Service is provided on an "as is" and "as available" basis. To the fullest extent permitted by law,
          Daily Groove disclaims all warranties, whether express, implied, or statutory, including warranties of
          merchantability, fitness for a particular purpose, non-infringement, availability, accuracy, and security.
          Daily Groove is not a medical, legal, financial, or other professional advisory service, and you should not
          rely on it for professional advice or emergency decisions.
        </p>
      </Section>

      <Section title="10. Limitation of liability">
        <p>
          To the fullest extent permitted by law, Daily Groove and its operator will not be liable for indirect,
          incidental, special, consequential, exemplary, or punitive damages, or for loss of data, profits, goodwill,
          or business interruption arising from or related to the Service. Where liability cannot be excluded, it will
          be limited to the greatest extent permitted by applicable law. Nothing in these Terms limits liability that
          cannot legally be limited.
        </p>
      </Section>

      <Section title="11. Governing law and disputes">
        <p>
          These Terms are governed by the laws that apply at the operator's principal place of business, without
          regard to conflict-of-law rules, except where the law of your residence requires otherwise. Before bringing
          a formal claim, please contact us through the <ContactLink /> and give us a reasonable opportunity to try to
          resolve the issue informally. Nothing in these Terms removes rights that applicable consumer-protection law
          does not allow us to remove.
        </p>
      </Section>

      <Section title="12. Changes to these Terms">
        <p>
          We may update these Terms to reflect changes in the Service, law, or operations. Updated Terms will be
          posted here with a revised effective date. If you continue using the Service after the updated Terms take
          effect, you agree to the revised Terms to the extent permitted by law.
        </p>
      </Section>

      <Section title="13. Contact">
        <p>
          For questions about these Terms, please use the operator's <ContactLink /> and identify your message as
          relating to Daily Groove Terms.
        </p>
      </Section>
    </LegalPage>
  );
}
